// @ts-nocheck
/**
 * ドラフトシステム - メインアプリケーション
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useDraft, useAutoDraftSave } from '../hooks';
import { DRAFT_STATUS } from '../constants';
import { DraftLobby } from './DraftLobby';
import { DraftPickScreen } from './DraftPickScreen';
import { DraftDeckBuilder } from './DraftDeckBuilder';
import { DraftTournament } from './DraftTournament';
import { DraftResult } from './DraftResult';
import { draftStyles } from './styles/draftStyles';
import { addToHistory, loadDraftSession, saveDraftSession } from '../systems/draftStorage';

// トーナメント状態の初期値
const INITIAL_TOURNAMENT_STATE = {
  matches: [],
  currentRound: 1,
  playerWins: 0,
  playerLosses: 0,
  isPlayerEliminated: false,
  tournamentComplete: false,
  pendingBattleResult: null,
  currentMatchId: null,
};

/**
 * ドラフトアプリケーション
 * @param {Object} props
 * @param {Object[]} props.allCards - 全カードデータ
 * @param {Map} [props.cardValueMap] - カード価値マップ
 * @param {Function} props.onExit - 終了コールバック
 * @param {Function} props.onStartBattle - バトル開始コールバック(playerDeck, opponentDeck, opponentName, onBattleEnd)
 */
export const DraftApp = ({
  allCards,
  cardValueMap = null,
  onExit,
  onStartBattle,
}) => {
  const {
    session,
    isLoading,
    error,
    status,
    startNewDraft,
    resumeDraft,
    cancelDraft,
    pickCard,
    addToDeck,
    removeFromDeck,
    confirmDeck,
    getSavedSummary,
    checkSavedSession,
    updateTournamentState,
    getTournamentState,
  } = useDraft(allCards, cardValueMap);

  // 自動保存
  useAutoDraftSave(session, { enabled: !!session });

  // 結果状態
  const [result, setResult] = useState(null);

  // ローカルトーナメント状態（セッションから初期化し、変更時にセッションに同期）
  const [tournamentState, setTournamentStateLocal] = useState(INITIAL_TOURNAMENT_STATE);

  // 結果表示済みフラグ（重複防止）
  const resultShownRef = useRef(false);

  // 復元済みフラグ（初回のみ復元、以降は無視）
  const restoredRef = useRef(false);

  // セッションからトーナメント状態を復元（初回マウント時のみ）
  useEffect(() => {
    // 既に復元済みの場合はスキップ（自分の更新で再トリガーされないように）
    if (restoredRef.current) {
      console.log('[DraftApp] Skipping restoration (already restored in this mount)');
      return;
    }

    const savedState = getTournamentState();
    if (savedState) {
      console.log('[DraftApp] Restoring tournament state from session (initial):', {
        currentMatchId: savedState.currentMatchId,
        pendingBattleResult: savedState.pendingBattleResult,
        matchesCount: savedState.matches?.length,
        currentRound: savedState.currentRound,
      });
      setTournamentStateLocal(savedState);
      restoredRef.current = true;
    }
  }, [getTournamentState, session?.sessionId]);

  // トーナメント状態をセッションに同期
  const setTournamentState = useCallback((updater) => {
    setTournamentStateLocal(prev => {
      const newState = typeof updater === 'function' ? updater(prev) : updater;
      // セッションに保存
      updateTournamentState(newState);
      return newState;
    });
  }, [updateTournamentState]);

  // バトル開始ハンドラ
  const handleStartBattle = useCallback((playerDeck, opponentDeck, opponentName, matchInfo) => {
    if (!onStartBattle) {
      console.warn('[DraftApp] onStartBattle not provided');
      return;
    }

    console.log('[DraftApp] Starting battle, matchId:', matchInfo.matchId);

    // 重要: React state更新は非同期のため、バトル開始前に直接localStorageに保存する
    // これにより、DraftAppがアンマウントされてもcurrentMatchIdが失われない
    try {
      const sessionData = loadDraftSession();
      if (sessionData?.state) {
        // トーナメント状態が無い場合は現在のローカル状態を使用
        if (!sessionData.state.tournament) {
          sessionData.state.tournament = { ...tournamentState };
        }
        sessionData.state.tournament.currentMatchId = matchInfo.matchId;
        sessionData.state.tournament.pendingBattleResult = null;
        saveDraftSession(sessionData.state);
        console.log('[DraftApp] Tournament state saved SYNC before battle:', {
          matchId: matchInfo.matchId,
          matchesCount: sessionData.state.tournament.matches?.length,
        });
      }
    } catch (e) {
      console.error('[DraftApp] Failed to save tournament state before battle:', e);
    }

    // ローカル状態も更新（次回レンダー用、実行されない可能性あり）
    setTournamentState(prev => ({
      ...prev,
      currentMatchId: matchInfo.matchId,
      pendingBattleResult: null,
    }));

    // メインゲームにバトル開始を依頼
    // 注: バトル結果はmagic-spirit.jsxが直接セッションに保存する
    onStartBattle(playerDeck, opponentDeck, opponentName);
  }, [onStartBattle, setTournamentState, tournamentState]);

  // トーナメント終了時
  const handleTournamentEnd = useCallback((tournamentResult) => {
    // 重複防止
    if (resultShownRef.current) return;
    resultShownRef.current = true;

    console.log('[DraftApp] Tournament ended:', tournamentResult);

    // 履歴に追加
    addToHistory({
      sessionId: session?.sessionId,
      playerCount: session?.config?.playerCount,
      ...tournamentResult,
    });

    setResult(tournamentResult);
  }, [session]);

  // 新規ドラフト開始時にトーナメント状態をリセット
  const handleStartNewDraft = useCallback((playerCount) => {
    // トーナメント状態をリセット
    setTournamentStateLocal(INITIAL_TOURNAMENT_STATE);
    resultShownRef.current = false;
    startNewDraft(playerCount);
  }, [startNewDraft]);

  // エラー表示
  if (error) {
    return (
      <div style={draftStyles.container}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          padding: '20px',
        }}>
          <h2 style={{ color: '#ff6b6b', marginBottom: '20px' }}>エラー</h2>
          <p style={{ color: '#ccc', marginBottom: '30px' }}>{error}</p>
          <button onClick={onExit} style={draftStyles.backButton}>
            戻る
          </button>
        </div>
      </div>
    );
  }

  // ローディング
  if (isLoading) {
    return (
      <div style={draftStyles.container}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
        }}>
          <p style={{ color: '#ffd700', fontSize: '1.2rem' }}>読み込み中...</p>
        </div>
      </div>
    );
  }

  // 結果画面
  if (result) {
    return (
      <DraftResult
        result={result}
        onNewDraft={() => {
          setResult(null);
          // トーナメント状態をリセット
          setTournamentStateLocal(INITIAL_TOURNAMENT_STATE);
          resultShownRef.current = false;
          cancelDraft();
        }}
        onBack={onExit}
      />
    );
  }

  // ステータスに応じた画面表示
  switch (status) {
    case DRAFT_STATUS.PICKING:
      return (
        <DraftPickScreen
          session={session}
          onPick={pickCard}
          onCancel={() => {
            if (window.confirm('ドラフトを中断しますか？\n進行状況は保存されます。')) {
              onExit?.();
            }
          }}
        />
      );

    case DRAFT_STATUS.BUILDING:
      return (
        <DraftDeckBuilder
          session={session}
          onAddToDeck={addToDeck}
          onRemoveFromDeck={removeFromDeck}
          onConfirm={confirmDeck}
        />
      );

    case DRAFT_STATUS.TOURNAMENT:
      return (
        <DraftTournament
          session={session}
          onStartBattle={handleStartBattle}
          onTournamentEnd={handleTournamentEnd}
          tournamentState={tournamentState}
          setTournamentState={setTournamentState}
        />
      );

    case DRAFT_STATUS.FINISHED:
      // 直接結果を設定（通常はトーナメント終了時に設定される）
      if (!result && !resultShownRef.current) {
        resultShownRef.current = true;
        setResult({
          placement: 1,
          wins: 0,
          losses: 0,
          totalPicks: session?.draft?.picks?.player?.length || 0,
        });
      }
      return null;

    default:
      // ロビー画面
      return (
        <DraftLobby
          onStartNew={handleStartNewDraft}
          onResume={() => {
            resultShownRef.current = false;
            resumeDraft();
          }}
          onBack={onExit}
          hasSavedSession={checkSavedSession()}
          savedSummary={getSavedSummary()}
        />
      );
  }
};

export default DraftApp;
